import { SortDirEnum } from "@/services/dropdown-enums";
import { MAX_FILE_MB } from "./constants";

export const DEFAULT_HISTORY_ORDER = [{ createdOn: "DESC" }];

/** Map UISimpleTable sortInfo → GraphQL LeadsImportHistorySortInput. */
export const toHistoryGraphqlOrder = (sortInfo) => {
  if (!sortInfo?.colName || !sortInfo?.dir) return DEFAULT_HISTORY_ORDER;
  const dir = sortInfo.dir === SortDirEnum.DESC ? "DESC" : "ASC";
  return [{ [sortInfo.colName]: dir }];
};

export const formatBytes = (bytes) => {
  if (bytes !== 0 && !bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const bytesToMb = (bytes) => bytes / 1024 / 1024;

const IMPORT_TITLE_DATETIME_SUFFIX =
  /\s+-\s+[A-Z][a-z]{2}\s+\d{1,2},\s+\d{4},\s+\d{1,2}:\d{2}\s+(AM|PM)$/i;

export const stripImportTitleDateTime = (title = "") =>
  String(title).replace(IMPORT_TITLE_DATETIME_SUFFIX, "").trim();

export const getTodayImportTitle = () => {
  const d = new Date();
  return `Import - ${d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })}`;
};

/** Auto name used when creating import right before upload. */
export const getAutoImportName = (file) => {
  const d = new Date();
  const datePart = d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  const base = (file?.name || "")
    .replace(/\.[^.]+$/, "")
    .trim()
    .slice(0, 60);
  return base ? `Import - ${base} - ${datePart}` : `Import - ${datePart}`;
};

export const formatImportDateTime = (date = new Date()) =>
  date.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

export const isAcceptedImportFile = (file) => {
  if (!file) return false;
  const byExt = /\.(csv|xlsx|xls)$/i.test(file.name || "");
  const type = file.type || "";
  const byMime =
    ["text/csv", "application/vnd.ms-excel"].some((t) => type.includes(t)) ||
    type.includes("spreadsheet");
  return byExt || byMime;
};

export const validateImportFile = (file) => {
  if (!file) {
    return { ok: false, error: "", file: null };
  }
  if (!isAcceptedImportFile(file)) {
    return {
      ok: false,
      error: "Please select a CSV or Excel file.",
      file: null,
    };
  }
  if (bytesToMb(file.size) > MAX_FILE_MB) {
    return {
      ok: false,
      error: `File size exceeded ${MAX_FILE_MB} MB.`,
      file: null,
    };
  }
  return { ok: true, error: "", file };
};

export const cloneList = (list = []) => list.map((item) => ({ ...item }));

export const LEAD_IMPORT_COMPLETED_TYPE = "LeadImportCompleted";

const parseNotificationData = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
};

/** Map a LeadImportCompleted notification into a wizard seed for the success step. */
export const mapLeadImportNotificationToSeed = (notification) => {
  const data = parseNotificationData(notification?.data);
  const recordId = data.recordId ?? data.RecordId ?? data.id;
  if (recordId == null || recordId === "") return null;

  const imported = Number(data.recordsCreatedCount) || 0;
  const updated = Number(data.recordsUpdatedCount) || 0;
  const total = Number(data.recordsCount) || 0;
  const processed = Number(data.processedRowsCount) || imported + updated;
  const skipped =
    data.skippedRowsCount != null
      ? Number(data.skippedRowsCount) || 0
      : Math.max(0, total - imported - updated);

  return {
    id: recordId,
    name: notification?.title || "Lead Import",
    openSuccess: true,
    status: data.status,
    successSummary: {
      total,
      imported,
      updated,
      skipped,
      processed,
    },
  };
};
